import {Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {map} from "rxjs/operators";
import {MvsCrudService} from "features/core/shared/service/crud/mvs-crud.service";
import {
    ProvisionManualBookingRequestDto,
    ProvisionManualBookingResultDto
} from "../../model/dto/api/provision-manual-booking.dto";
import {ProvisionTransferRequestDto, ProvisionTransferResultDto} from "../../model/dto/api/provision-transfer.dto";
import {
    ProvisionAccountStatementRequestDto,
    ProvisionAccountStatementResponseDto,
    ProvisionContractTransactionDetailDto
} from "../../model/dto/api/provision-account-statement.dto";

/**
 * Custom API service for cp.ProvisionAgentTransaction action endpoints
 * (03-architektur-backend.md §11.1). Base URL `/mvsa/cp/provisionAgentTransactions` — identical to
 * the route the alias `cp.ProvisionAgentTransaction` carries in cp.entity-provider.ts.
 *
 * Both actions are two of the three design-gate action dialogs (capability-matrix.md §5 B-9); the
 * third is the agent change on CpProvisionContractAgentService. NO handwritten form HTML anywhere: each
 * dialog builds an `MvsFormDto` in the frontend and hands it to a TRANSIENT FORM WIDGET
 * (`WidgetFactory.createWidget(alias, name, 'form', 'transient', 'transient', objectType,
 * WidgetDataParam.create('form', form))` -> `<mvs-widget (onGenericEvent)="…">`), the mechanism the
 * repo uses in `features/feature-core/wf/component/protected-components/wf-process-start/
 * wf-process-start.component.ts`. The write itself belongs to the caller and goes to the custom
 * endpoints below — see the Javadoc of CpProvisionAgentManualBookingDialogComponent and
 * CpProvisionAgentTransferDialogComponent.
 *
 * A business failure (e.g. insufficient reserve coverage) arrives as HTTP 200 with success = false
 * (bm BmContractFrequencyChangeResultDto convention) — callers MUST read `success`, never assume
 * 2xx means applied.
 */
@Injectable({
    providedIn: 'root'
})
export class CpAgentTransactionService extends MvsCrudService {

    constructor(protected override http: HttpClient) {
        super(http, MvsCrudService.baseUrl + '/cp/provisionAgentTransactions');
    }

    /**
     * POST /manualBooking — creates an ACTIVE account booking (V-007.1); when
     * provisionContractId is set, also creates the R-09 contract-side counterpart
     * (V-007.2). Requires um right `cp_manualBooking`.
     */
    public manualBooking(request: ProvisionManualBookingRequestDto): Observable<ProvisionManualBookingResultDto> {
        return this.http.post<ProvisionManualBookingResultDto>(this.apiUrl + '/manualBooking', request);
    }

    /**
     * POST /transfer — books an atomic Rücklage&lt;-&gt;Provision transfer pair (event
     * RESERVE_TRANSFER, U-03/KF-08). Requires um right `cp_reserveTransfer`.
     */
    public transfer(request: ProvisionTransferRequestDto): Observable<ProvisionTransferResultDto> {
        return this.http.post<ProvisionTransferResultDto>(this.apiUrl + '/transfer', request);
    }

    /**
     * POST /withBalance — the account statement of ONE agent on ONE of its two accounts: a page of
     * booking lines with their running balance, plus the persisted head figures.
     *
     * <h2>The delivered numbers are final</h2>
     * `signedAmount` and `runningBalance` arrive SIGNED, folded over the module's single sign
     * definition (`ProvisionBalanceService.signedAmount`). Callers colour and total by the SIGN OF
     * THOSE VALUES; `bookingKind` is a label for the direction column and must never be turned
     * back into a sign in TypeScript (design gate FD-1). Likewise `currentBalance` /
     * `simulatedBalance` are READ from the persisted `ProvisionAgentBalance` (decision D4) — do
     * not re-aggregate them from the returned lines.
     *
     * <h2>Errors keep their German text</h2>
     * A refused request (missing agent, an anchor belonging to a different agent or account) comes
     * back as HTTP 400 carrying a German sentence from `ProvisionCheckRefusedException` via
     * `CpControllerExceptionAdvice`, shaped as `CoreApiResponseDto` with the sentence in `data`.
     * This method deliberately adds NO `catchError`: the global `HttpErrorInterceptor` already
     * toasts `error.error.data` and re-throws the untouched `HttpErrorResponse`, so callers that
     * want the sentence inline read `error.error?.data`. Replacing it with a generic text would
     * destroy the only information the caseworker can act on.
     *
     * @param request agent, account, scope, period and the paging anchor
     * @return the page and the head figures of the account
     */
    public getAccountStatement(request: ProvisionAccountStatementRequestDto): Observable<ProvisionAccountStatementResponseDto> {
        return this.http.post<ProvisionAccountStatementResponseDto>(this.apiUrl + '/withBalance', request);
    }

    /**
     * POST /withBalance/detail/{transactionId} — the contract side of ONE account booking, loaded
     * lazily when its row is expanded.
     *
     * <h2>Why this returns `null` instead of failing</h2>
     * An account booking is NOT required to have a contract counterpart: a manual booking or a
     * payout has none. The backend expresses that as HTTP 204 No Content — a valid answer, not an
     * error. The `observe: 'response'` read below makes that explicit rather than leaning on the
     * empty-body-to-null coercion of `HttpClient`, and maps the 204 onto `null` so the caller can
     * render an informational note ("Zu dieser Buchung gibt es kein Vertragspendant.") instead of
     * an error state. Use `hasContractDetail` on the line to decide whether to call at all.
     *
     * Genuine failures are untouched: a 403 (the booking belongs to a different agent — the FD-4
     * owner check) or a 400 propagates as an `HttpErrorResponse` with its German message intact,
     * exactly as described on {@link getAccountStatement}.
     *
     * @param transactionId id of the `cp.ProvisionAgentTransaction` whose row was expanded
     * @return the contract detail, or `null` when the booking has no contract counterpart (204)
     */
    public getContractTransactionDetail(transactionId: number): Observable<ProvisionContractTransactionDetailDto | null> {
        return this.http
            .post<ProvisionContractTransactionDetailDto>(
                this.apiUrl + '/withBalance/detail/' + transactionId,
                {},
                {observe: 'response'})
            .pipe(map(response => response.status === 204 ? null : (response.body ?? null)));
    }

}
